import { gql } from '@apollo/client'
import { Chip, Heading, Link, Snippet } from '@farol-ds/react'
import { useEffect, useRef } from 'react'

import { useMetrics } from '../../hooks'
import { DocumentStatus } from '../../types'
import { RelatedLegalUnderstanding_DocumentFragment } from './__generated__/related-legal-understanding.graphql'
import styles from './related-legal-understanding.module.scss'

export interface RelatedLegalUnderstandingProps {
  document: RelatedLegalUnderstanding_DocumentFragment
  heading?: string
}

type ThesisItem = NonNullable<
  NonNullable<
    NonNullable<RelatedLegalUnderstanding_DocumentFragment['relatedLegalUnderstanding']>['items']
  >[number]
>['thesis']

const FROM_COMPONENT = 'RelatedLegalUnderstanding'

export function RelatedLegalUnderstanding(props: RelatedLegalUnderstandingProps) {
  const { document, heading = 'Tese a qual pertence o julgado' } = props
  const relatedLegalUnderstanding = document?.relatedLegalUnderstanding?.items || []

  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()

  const eventTriggered = useRef(false)

  function handleClick({ index }: { index: number }) {
    sendMetrics('Doc.ElementClicked', {
      from_component: FROM_COMPONENT,
      metadata: {
        position: index,
      },
    })
  }

  useEffect(() => {
    if (relatedLegalUnderstanding.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!eventTriggered.current && entries[0]?.isIntersecting) {
          eventTriggered.current = true

          sendMetrics('Doc.ElementViewed', {
            from_component: FROM_COMPONENT,
          })

          observer.disconnect()
        }
      },
      { threshold: 0.25 }
    )
    observer.observe(dispatcherRef.current as HTMLDivElement)

    return () => {
      observer.disconnect()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.relatedLegalUnderstanding])

  if (!relatedLegalUnderstanding.length) return null

  function getCaptions(thesis: ThesisItem): (string | JSX.Element)[] {
    const captions: (string | JSX.Element)[] = ['Coletânea']

    const startDate = thesis?.yearsRange?.start || 0
    const endDate = thesis?.yearsRange?.end || 0
    if (startDate > 0 && endDate > 0) {
      if (startDate === endDate) {
        captions.push(`Julgados de ${startDate}`)
      } else {
        captions.push(`Julgados de ${startDate} - ${endDate}`)
      }
    }

    const relatedDocumentsCount = thesis?.relatedDocuments?.filter(
      (doc) => doc?.status === DocumentStatus.ATIVO
    ).length

    if (relatedDocumentsCount) {
      captions.push(
        `Embasado por ${relatedDocumentsCount} julgado${relatedDocumentsCount > 1 ? 's' : ''}`
      )
    }

    const isJurisprudenciaEmTeses = thesis?.author == 'Secretaria do STJ'
    if (isJurisprudenciaEmTeses) {
      captions.push(
        <Chip kind="info" size="sm">
          <span>Extraído da Secretaria de Jurisprudência do STJ</span>
        </Chip>
      )
    }

    return captions
  }

  return (
    <div className={styles.wrapper} data-testid="related-legal-understanding">
      <Heading size="xl" className={styles.heading} asChild>
        <h2>{heading}</h2>
      </Heading>
      <section ref={dispatcherRef}>
        {relatedLegalUnderstanding.map(
          (item, index) =>
            item?.thesis?.text &&
            item?.thesis?.networkDocument?.title &&
            item?.thesis?.networkDocument?.url && (
              <Item
                key={index}
                title={item.thesis.networkDocument.title}
                url={item.thesis.networkDocument.url}
                text={item.thesis.text}
                captions={getCaptions(item.thesis)}
                onClick={() => handleClick({ index })}
              />
            )
        )}
      </section>
    </div>
  )
}

export interface ItemProps {
  title: string
  text: string
  url: string
  captions: (string | JSX.Element)[]
  onClick: () => void
}

export const Item = (props: ItemProps) => (
  <Snippet className={styles.snippet} data-from-component={FROM_COMPONENT}>
    <Snippet.Container>
      <Snippet.Header>
        <Snippet.Title size="sm" data-testid="snippet-item-title" asChild>
          <h3>
            <Link href={props.url} onClick={props.onClick} type="primary">
              {props.title}
            </Link>
          </h3>
        </Snippet.Title>
        <Snippet.Caption>
          {props.captions.map((part, index) => (
            <Snippet.CaptionItem key={index}>{part}</Snippet.CaptionItem>
          ))}
        </Snippet.Caption>
      </Snippet.Header>
      <Snippet.Body>
        <Snippet.Content label="Tese">
          {!!props.text && <p dangerouslySetInnerHTML={{ __html: props.text }} />}
        </Snippet.Content>
      </Snippet.Body>
    </Snippet.Container>
  </Snippet>
)

RelatedLegalUnderstanding.fragments = {
  document: gql`
    fragment RelatedLegalUnderstanding_document on Document {
      relatedLegalUnderstanding(
        filter_author: "Secretaria do STJ"
        static_search: true
        query: $legalUnderstandingQuery
        filterByCurrentDocId: $legalUnderstandingFilterByCurrentDocId
      ) {
        items {
          thesis {
            text
            author

            relatedDocuments {
              status
            }

            yearsRange {
              start
              end
            }

            networkDocument {
              url
              title
            }
          }
        }
      }
    }
  `,
}
